import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Breadcrumbs from 'components/Breadcrumbs'
import ExtraLargeHeader from 'components/ExtraLargeHeader'
import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'

import { BreadcrumbEnum } from 'types/enums'

import Anchor from './Anchor'
import Entry from './Entry'
import * as T from './types'

const MaxWidthContainer = styled.div`
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  margin: 0 auto;
`

const BreadcrumbsContainer = styled.div`
  padding: 0 15px;
`

const AnchorContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 ${props => props.theme.metrics.gutterPhone}px;
`

const AnchorHeader = styled.h3`
  margin: 0 20px;
  padding: 20px 0 30px;
  font-weight: 900;
  font-size: 22px;
  line-height: 22px;
  color: ${props => props.theme.colors.black};
`

const EntryContainer = styled.div`
  margin: 50px ${props => props.theme.metrics.gutterPhone}px 100px;
`

const SectionContainer = styled.div`
  padding: 0 20px 0 0;
  margin: 20px 0;
  background-color: ${props => props.theme.colors.white};
  border-radius: 3px;
`

// Quick way to sort to client spec without adding `order` to faq anchor model
// we can revisit in the future
//
// NB: some of these might not appear locally, these are prod values
const ORDERED_ANCHOR_NAMES = [
  'basics',
  'create listing',
  'pricing & commissions',
  'mls',
  'contracts & disclosures',
  'homelister app',
  'fmls listings',
  'marketing my home',
  'open houses & showings',
  'selling my home',
  'state specific requirements and fees',
]

const showAllAnchor: T.IAnchor = {
  anchor: 'showAll',
  title: 'Show All',
  entries: [],
}

interface IProps {
  anchors: T.IAnchor[]
  queryAnchor: string
  currentAnchor: T.IAnchor | undefined
  selectedAnchor: String
  selectAnchor(anchor: string): void
}

const FAQ: FunctionComponent<IProps> = ({
  anchors,
  queryAnchor,
  currentAnchor,
  selectedAnchor,
  selectAnchor,
}) => {
  const renderEntry = (entity: T.IEntry, last: boolean) => (
    <Entry
      key={entity.anchor}
      entry={entity}
      last={last}
      queryAnchor={queryAnchor}
    />
  )

  const renderAnchorSection = (anchor: T.IAnchor) =>
    anchor.anchor !== showAllAnchor.anchor && (
      <SectionContainer key={anchor.anchor}>
        <AnchorHeader>{anchor.title}</AnchorHeader>
        {anchor.entries.map((entry, idx) => renderEntry(entry, idx === anchor.entries.length - 1))}
      </SectionContainer>
    )

  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()

  return (
    <MaxWidthContainer>
      <Spacer
        size={24}
        responsiveSize={0}
      />
      <BreadcrumbsContainer>
        <Breadcrumbs
          name={BreadcrumbEnum.FAQ}
          hideMobile
          noMargin
        />
      </BreadcrumbsContainer>
      <Spacer
        size={5}
        tabletSize={25}
        responsiveSize={25}
      />
      <ExtraLargeHeader
        title={width && width >= theme.metrics.tablet ? 'Frequently Asked Questions' : 'FAQ'}
        titleSize={32}
        titleDesktopSize={42}
        titleWeight={500}
        noMargin
      />
      <Spacer
        size={30}
        responsiveSize={25}
      />
      <AnchorContainer>
        <Anchor
          key="showAll"
          anchor={showAllAnchor}
          selectedAnchor={selectedAnchor}
          selectAnchor={selectAnchor}
        />
        {ORDERED_ANCHOR_NAMES.map((anchorName: string) => {
          const anchor = anchors.find(
            anchorEntry => anchorName === anchorEntry.title.toLocaleLowerCase(),
          )
          if (!anchor) return null

          return (
            <Anchor
              key={anchor.anchor}
              anchor={anchor}
              selectedAnchor={selectedAnchor}
              selectAnchor={selectAnchor}
            />
          )
        })}
      </AnchorContainer>
      <EntryContainer>
        {currentAnchor?.anchor === showAllAnchor.anchor
          && anchors.map(anchor => renderAnchorSection(anchor))}
        {currentAnchor && renderAnchorSection(currentAnchor)}
      </EntryContainer>
    </MaxWidthContainer>
  )
}

export default FAQ
